/**
 * CpuParticleAdapter — wraps the existing CPU `ParticleEmitter` (+ its
 * `ParticleRenderer`) behind the common `IParticleEmitter` interface.
 *
 * This is the fallback the `GpuParticleModule` hands back when WebGPU is
 * unavailable, so callers that asked for a GPU emitter still get something that
 * satisfies `IParticleEmitter` and renders. The wrapped CPU class is NOT
 * modified — this adapter only adds `getActiveCount()` + `backend` on top of
 * its existing public members.
 *
 * The CPU emitter simulates on the CPU and needs a camera to billboard each
 * frame, so unlike the GPU emitter its `update(dt)` cannot fully render on its
 * own. The adapter exposes `render(camera)` for the system to call; if you use
 * the normal `ParticleSystem` instead, register the underlying emitter there.
 *
 * WORLD-SPACE CONTRACT (do not parent under a transformed Object3D):
 * With the default `simulationSpace: 'world'`, the wrapped `ParticleEmitter`
 * bakes the emitter's `worldMatrix` into each particle's spawn position and the
 * `ParticleRenderer` writes those WORLD positions directly into instance
 * matrices. The `mesh` therefore expects to live at the SCENE ROOT (identity
 * world transform) — `GpuParticleModule._parentMesh` enforces this. Parenting
 * it under an entity with a non-identity transform double-applies the model
 * matrix. With `simulationSpace: 'local'`, positions stay emitter-local and the
 * mesh may be parented under the entity to inherit its transform.
 */

import type * as THREE from 'three/webgpu';
import { ParticleEmitter } from '../ParticleEmitter.js';
import { ParticleRenderer } from '../ParticleRenderer.js';
import type { ParticleConfig } from '../types.js';
import type { GpuParticleConfig, IParticleEmitter, ParticleBackend } from './types.js';

export class CpuParticleAdapter implements IParticleEmitter {
  readonly backend: ParticleBackend = 'cpu';
  readonly emitter: ParticleEmitter;
  readonly renderer: ParticleRenderer;
  /** The renderable mesh (matches GpuParticleEmitter.mesh shape). */
  readonly mesh: THREE.InstancedMesh;

  constructor(config?: Partial<GpuParticleConfig>) {
    // Resolve gravity the SAME way GpuParticlePool.configureForces does
    // (forces.gravityModifier ?? gravityModifier ?? 0). The base CPU
    // ParticleConfig has no `forces` field, so a GpuParticleConfig that puts
    // gravity ONLY under `forces` would otherwise read as zero gravity on the
    // WebGL2 CPU fallback — silent divergence from the WebGPU path. We fold it
    // into the plain `gravityModifier` the CPU emitter understands. Plain
    // CPU-emitter users (no `forces`) are unaffected.
    const cpuConfig = foldForcesGravity(config);
    this.emitter = new ParticleEmitter(cpuConfig);
    this.renderer = new ParticleRenderer(this.emitter.config);
    this.mesh = this.renderer.mesh;
  }

  get config(): ParticleConfig { return this.emitter.config; }
  get maxParticles(): number { return this.emitter.maxParticles; }
  get playing(): boolean { return this.emitter.playing; }
  get worldMatrix(): THREE.Matrix4 { return this.emitter.worldMatrix; }

  play(): void { this.emitter.play(); }
  pause(): void { this.emitter.pause(); }
  stop(): void { this.emitter.stop(); }
  emit(count: number): void { this.emitter.emit(count); }
  update(dt: number): void { this.emitter.update(dt); }
  getActiveCount(): number { return this.emitter.activeCount; }
  setEmissionRate(rate: number): void { this.emitter.setEmissionRate(rate); }

  /** Push current particle state into the instanced mesh (CPU billboarding). */
  render(camera: THREE.Camera): void {
    this.renderer.update(this.emitter, camera);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.renderer.dispose();
  }
}

/**
 * Map a `GpuParticleConfig` to a plain `Partial<ParticleConfig>` for the CPU
 * emitter, resolving gravity from `forces.gravityModifier` (GPU-only field) so
 * the CPU fallback matches the GPU path. Returns the input unchanged when there
 * is no `forces` block (keeps plain CPU-emitter users' configs untouched).
 */
function foldForcesGravity(
  config?: Partial<GpuParticleConfig>,
): Partial<ParticleConfig> | undefined {
  const forces = config?.forces;
  if (!config || forces?.gravityModifier === undefined) return config;
  // Copy-then-delete instead of rest-destructuring: keeps lint clean (no
  // unused `_drop` binding) while still omitting the GPU-only `forces` block.
  const rest = { ...config };
  delete rest.forces;
  return { ...rest, gravityModifier: forces.gravityModifier };
}
